import React, { useEffect, useState } from "react";

import { adminTicketsService } from "api/services/tickets.service";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";

import type { AdminTicketDetail } from "api/types/tickets.types";

import "./TicketRefundModal.scss";

const MAX_REASON_LENGTH = 200;

interface TicketRefundModalProps {
  open: boolean;
  ticket: AdminTicketDetail | null;
  onClose: () => void;
  onRefunded?: () => void;
}

const TicketRefundModal: React.FC<TicketRefundModalProps> = ({ open, ticket, onClose, onRefunded }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);

  const handleRefundSubmit = async () => {
    if (!ticket) return;
    if (ticket.order_id == null) {
      setError("연결된 주문 정보가 없어 환불할 수 없습니다.");
      return;
    }
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setError("취소 사유를 입력해 주세요.");
      return;
    }
    if (trimmed.length > MAX_REASON_LENGTH) {
      setError(`취소 사유는 최대 ${MAX_REASON_LENGTH}자까지 입력할 수 있습니다.`);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await adminTicketsService.refund(ticket.order_id, trimmed);
      onRefunded?.();
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="수강권 환불"
      width="480px"
      onClose={submitting ? undefined : onClose}
      closeOnOverlayClick={!submitting}
      footer={
        <div className="ticket-refund__footer">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            닫기
          </Button>
          <Button variant="danger" onClick={handleRefundSubmit} disabled={submitting}>
            환불하기
          </Button>
        </div>
      }
    >
      <div className="ticket-refund">
        {error && <div className="ticket-refund__error">{error}</div>}
        <p className="ticket-refund__notice">주문을 취소하고 결제 전액을 환불합니다. 환불 후에는 되돌릴 수 없습니다.</p>
        <label className="ticket-refund__label" htmlFor="ticket-refund-reason">
          취소 사유
        </label>
        <textarea id="ticket-refund-reason" className="ticket-refund__textarea" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={MAX_REASON_LENGTH} rows={4} placeholder="취소 사유를 입력하세요 (최대 200자)" disabled={submitting} />
        <div className="ticket-refund__count">
          {reason.length} / {MAX_REASON_LENGTH}
        </div>
      </div>
    </Modal>
  );
};

export default TicketRefundModal;
